// src/data/services/jasa-instalasi-jaringan-cctv.tsx

"use client";
import { ServiceSection } from "@/components/ServiceSection";
import { site } from "@/data/default";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import Image from "next/image";

import "swiper/css/pagination";
import "swiper/css";

const JasaInstalasiPanelSuryaContent = (
  <>
    {/* DESKRIPSI */}
    <ServiceSection id="deskripsi" title="Jasa Instalasi Panel Surya & Pengadaan Perangkat">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h1 className="text-3xl font-bold leading-tight mb-4">
            Tagihan Listrik Bengkak? Nyut²an?
            <span className="text-purple block mt-2">
              Panel Surya = Energi Stabil, Biaya Tetap, Kontrol Penuh.
            </span>
          </h1>
          <p className="mb-4">
            Pernah hitung berapa total biaya listrik dalam 5 tahun terakhir? Pasti gila kan?<br />
            Di situlah kerennya kami. Solusi energi berbasis panel surya yang efisien, scalable, dan terintegrasi. Instalasi cepat, desain rapi, perangkat resmi, hasil bisa langsung dipantau dari HP.
          </p>
          <ul className="text-sm space-y-2 mb-6 text-muted-foreground">
            <li>✅ Survey lokasi GRATIS + simulasi output energi</li>
            <li>✅ Sistem On-Grid, Off-Grid, atau Hybrid sesuai kebutuhan</li>
            <li>✅ Laporan desain, wiring diagram, dan SOP penggunaan</li>
            <li>✅ Area layanan: Jakarta, Bekasi, Depok, Tangerang, Bogor</li>
          </ul>
          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%2C%20saya%20tertarik%20pasang%20panel%20surya.%20Boleh%20dibantu%20cek%20lokasi%20dan%20kapasitas%20yang%20dibutuhkan%3F`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-purple-600 to-orange-600 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition"
          >
            🚀 Konsultasi Gratis. Sharelok aja!
          </a>
        </div>
        <div className="hidden md:block">
          <Image
            src="/images/services/instalasi-panel-surya.webp"
            alt="Instalasi Panel Surya"
            className="w-full max-w-md mx-auto"
            width={1024}
            height={1024}
          />
        </div>
      </div>
    </ServiceSection>

    {/* EDUKASI */}
    <ServiceSection id="edukasi" title="Kenapa Instalasi Panel Surya Harus Ditangani Profesional?">
      <div className="space-y-4 text-sm">
        <p>
          Panel surya yang asal pasang = <strong>investasi yang nggak balik modal.</strong><br />
          Sistem tanpa perhitungan akurat = <strong>daya drop & boros tanpa disadari.</strong>
        </p>
        <p>
          Faktanya, banyak pengguna kecewa karena sistemnya gagal memenuhi ekspektasi. Padahal ini bisa dihindari kalau tim instalasinya ngerti hitung²an teknis, kombinasi <strong>desain teknis + kebutuhan nyata pengguna.</strong>
        </p>
        <ul className="list-disc ml-6">
          <li><strong>Perhitungan kapasitas</strong> sesuai kebutuhan listrik harian (bukan tebak-tebakan)</li>
          <li><strong>Orientasi & kemiringan panel</strong> disesuaikan arah matahari dan posisi bangunan</li>
          <li><strong>Sistem proteksi & inverter</strong> yang kompatibel dan aman jangka panjang</li>
        </ul>
        <p>
          Kami jelaskan semua ini ke kamu dalam bahasa awam. Jadi, setelah sistem aktif, kamu paham cara kerjanya dan tahu cara mengontrolnya sendiri.
        </p>
      </div>
    </ServiceSection>

    {/* KEUNGGULAN */}
    <ServiceSection id="keunggulan" title="Apa Bedanya Kami dengan Tukang Pasang Biasa?">
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {[{
          title: "🧠 Rancang Sistem, Bukan Cuma Pasang",
          desc: "Setiap titik, dari panel ke inverter hingga baterai, dihitung berdasarkan data harian & kebutuhan aktual. Bukan asal beli & colok."
        },
        {
          title: "📊 Semua Terintegrasi & Bisa Dipantau",
          desc: "Listrik terpantau real-time lewat dashboard di HP. Konsumsi, output, bahkan performa panel, semua transparan, tanpa biaya langganan."
        },
        {
          title: "📂 Dokumen Teknis Lengkap",
          desc: "Skema listrik, konfigurasi sistem, dan SOP operasional kamu pegang. Jadi, kamu tetap tenang meski teknisi sudah selesai kerja."
        },
        {
          title: "🤝 Lebih dari Vendor: Solusi Jangka Panjang",
          desc: "Kami hadir bukan buat pasang & pergi. Tapi jadi partner energi masa depan, yang bantu kamu hemat, stabil, dan mandiri secara listrik."
        }].map((item, i) => (
          <div
            key={i}
            className="group flex flex-col justify-between p-6 border rounded-xl shadow-md bg-gradient-to-br from-white to-gray-50 hover:from-purple-50 hover:to-white transition-all duration-300 hover:shadow-xl"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">{item.title.split(" ")[0]}</div>
              <div>
                <h3 className="text-lg font-semibold mb-1">
                  {item.title.split(" ").slice(1).join(" ")}
                </h3>
                <p className="text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* LAYANAN */}
    <ServiceSection id="layanan" title="Layanan Kami">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {[
          {
            title: "Instalasi Panel Surya (Solar PV)",
            desc: "Perencanaan & pemasangan panel surya On-Grid, Off-Grid, atau Hybrid untuk rumah, kantor, hingga industri. Lengkap dengan inverter & sistem monitoring.",
            tags: ["Solar Panel", "Inverter", "KWH Meter"]
          },
          {
            title: "Survey Lokasi & Simulasi Daya",
            desc: "Analisa intensitas cahaya matahari, sudut atap, serta simulasi output harian. Hasilnya digunakan untuk menentukan kapasitas ideal.",
            tags: ["Sun Path", "PV Simulation", "Lux Meter"]
          },
          {
            title: "Instalasi Baterai & Backup System",
            desc: "Pemasangan sistem penyimpanan energi (ESS) untuk kebutuhan darurat atau penggunaan malam hari. Cocok untuk wilayah dengan listrik tidak stabil.",
            tags: ["Lithium Battery", "ESS", "Backup"]
          },
          {
            title: "Monitoring & Integrasi IoT",
            desc: "Pantau performa panel, inverter, dan konsumsi listrik real-time via dashboard. Bisa diakses dari HP atau laptop tanpa biaya tambahan.",
            tags: ["Dashboard", "Realtime Data", "IoT"]
          },
          {
            title: "Upgrade & Perluasan Sistem",
            desc: "Punya sistem lama yang underperform? Kami bantu audit & upgrade kapasitas panel, inverter, atau sistem penyimpanan yang lebih efisien.",
            tags: ["Upgrade", "Reconfigure", "Efficiency"]
          },
          {
            title: "Perawatan & Troubleshooting",
            desc: "Cek performa menurun, panel kotor, inverter error? Tim kami siap inspeksi dan perbaiki tanpa harus bongkar ulang.",
            tags: ["Maintenance", "Clean Panel", "Service"]
          }
        ].map((item, i) => (
          <article
            key={i}
            className="relative group p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out h-full flex flex-col justify-between"
            itemScope
            itemType="https://schema.org/Service"
          >
            <div>
              <h3 itemProp="name" className="text-xl font-bold group-hover:text-purple-600 transition-colors">
                {item.title}
              </h3>
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {item.tags.map((tag, t) => (
                  <span
                    key={t}
                    className="text-xs font-medium bg-purple-100 text-purple-800 px-2 py-1 rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p itemProp="description" className="text-sm leading-relaxed">
                {item.desc}
              </p>
            </div>
          </article>
        ))}
      </div>
    </ServiceSection>

    {/* STUDI KASUS */}
    <ServiceSection id="studi-kasus" title="Studi Kasus Proyek Nyata">
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {[
          {
            title: "Instalasi Panel Surya Rumah Tinggal (Depok)",
            client: "Rumah 2 lantai, orientasi barat-timur",
            tags: ["3KWp", "Hybrid System", "Monitoring via App"],
            result: "Penghuni hemat hingga 60% tagihan listrik bulanan. Sistem otomatis beralih ke baterai saat PLN padam."
          },
          {
            title: "Solar Panel untuk Kantor Konsultan (Jakarta Selatan)",
            client: "Office space 200m², rooftop dak beton",
            tags: ["5KWp", "On-Grid", "Net Metering"],
            result: "Energi siang hari terserap penuh oleh aktivitas kantor. Kelebihan daya dikirim ke PLN untuk dikompensasi."
          },
          {
            title: "Instalasi Panel Surya di Workshop Otomotif (Bekasi)",
            client: "Bengkel + area cuci mobil",
            tags: ["6KWp", "Off-Grid", "Battery Storage"],
            result: "Bengkel tetap jalan meskipun listrik PLN sering padam. Sistem bisa berjalan mandiri hingga 10 jam nonstop."
          },
          {
            title: "Audit & Upgrade Sistem Lama (Tangerang)",
            client: "Perumahan cluster, sistem existing underperform",
            tags: ["Rewiring", "Efisiensi 40%", "Integrasi Monitoring"],
            result: "Tim kami optimalkan arah panel dan ganti inverter. Sekarang output meningkat signifikan tanpa ganti panel baru."
          }
        ].map((item, i) => (
          <div key={i} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition duration-200">
            <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
            <p className="text-sm text-muted-foreground italic mb-2">{item.client}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {item.tags.map((tag, t) => (
                <span key={t} className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md font-medium">
                  {tag}
                </span>
              ))}
            </div>
            <p className="text-sm">{item.result}</p>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* HARGA */}
    <ServiceSection id="harga" title="Harga Fleksibel, Sesuai Kebutuhan & Skala Proyek">
      <p className="text-sm mb-12">
        Setiap bangunan, kebutuhan energinya beda. Karena itu, harga kami bukan template, tapi hasil kalkulasi cerdas dari tim engineer kami. <br />
        Kamu bisa mulai dari paket hemat untuk rumah, sampai sistem skala pabrik, semuanya transparan, bisa disesuaikan, dan tanpa biaya tersembunyi.
      </p>

      <div className="grid md:grid-cols-3 gap-4 my-10">
        {/* PAKET HEMAT */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">🌞 Paket Hemat</h3>
            <p className="text-3xl font-bold mb-4">Rp8 Juta~</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ 1.5KWp - 2KWp (Rumah Kecil)</li>
              <li>✅ Hemat 30-50% tagihan listrik</li>
              <li>✅ Monitoring via HP</li>
              <li>✅ Estimasi instalasi: 1 hari</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Untuk rumah tinggal, toko kecil, atau pemula yang mau coba panel surya tanpa repot.
          </p>
        </div>

        {/* PAKET KANTOR */}
        <div className="flex flex-col justify-between relative border-2 border-purple-600 rounded-3xl p-6 bg-gradient-to-b from-purple-50 to-white shadow-lg hover:shadow-2xl transition-all duration-300 scale-[1.03] z-10">
          <div className="absolute -top-3 right-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full shadow">
            ⭐ Paling Banyak Dipilih
          </div>
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2 text-purple-700">🔋 Paket Skala Menengah</h3>
            <p className="text-3xl font-bold text-purple-600 mb-4">Rp18 Juta~</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ 3KWp - 5KWp (Kantor / Rumah 2 Lantai)</li>
              <li>✅ Net Metering (kirim daya ke PLN)</li>
              <li>✅ Siap ekspansi + integrasi baterai</li>
              <li>✅ Estimasi instalasi: 2 hari</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Cocok untuk rumah besar, kantor, atau bisnis yang ingin optimasi biaya listrik jangka panjang.
          </p>
        </div>

        {/* PAKET PROYEK */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">🏭 Paket Proyek Skala Besar</h3>
            <p className="text-3xl font-bold text-indigo-600 mb-4">Rp50 Juta+</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ 10KWp+ (Pabrik, Gudang, Gedung)</li>
              <li>✅ Desain topologi sistem kompleks</li>
              <li>✅ Sewa opsional + backup baterai besar</li>
              <li>✅ Estimasi instalasi: 3-5 hari</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Untuk proyek skala industri atau bangunan yang butuh energi stabil, efisien, dan sustainable.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-10">
        <p className="text-sm mt-8">
          Lagi hitung-hitungan tapi nggak yakin mulai dari mana? Atau takut salah pilih kapasitas?<br />
          Tenang, tim kami bantu hitung + survei dulu, nggak bayar, nggak ribet.
        </p>
        <p className="text-sm mb-2">Mau konsultasi dulu atau butuh simulasi panelnya?</p>
        <a
          href={`https://wa.me/${site.whatsapp}?text=Halo%2C%20saya%20mau%20konsultasi%20panel%20surya%20untuk%20rumah%20atau%20kantor`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gradient-to-r from-indigo-600 via-purple-600 to-orange-500 text-white text-sm px-6 py-3 rounded-full shadow-lg hover:scale-105 transition"
        >
          🚀 Konsultasi Gratis Sekarang → Kami Bantu Hitungin!
        </a>
      </div>
    </ServiceSection>

    {/* FAQ */}
    <ServiceSection id="faq" title="FAQ - Pertanyaan Umum Tentang Panel Surya">
      <div className="space-y-4">
        {[
          {
            question: "Kenapa harus panel surya? Listrik PLN juga masih aman.",
            answer:
              "Betul, PLN aman. Tapi tagihan listrik bulananmu bakal terus naik, sementara energi matahari itu gratis dan bisa kamu manfaatkan seumur hidup. Panel surya = investasi yang mengurangi tagihan, menambah nilai properti, dan ikut jaga bumi."
          },
          {
            question: "Berapa biaya pasang solar panel di rumah?",
            answer:
              "Mulai dari Rp7 jutaan untuk sistem sederhana (rumah 1 lantai, beban ringan). Harga tergantung daya yang dibutuhkan, sistem off-grid atau on-grid, dan luas atap. Konsultasi gratis, kami bantu hitung kapasitas ideal."
          },
          {
            question: "Apa bisa pasang sendiri tanpa teknisi?",
            answer:
              "Bisa, tapi risiko tinggi. Instalasi tanpa perhitungan = potensi korsleting, overheat, atau kerusakan inverter. Tim kami sudah tersertifikasi, dan setiap proyek melalui simulasi teknis sebelum dipasang."
          },
          {
            question: "Butuh izin atau legalitas khusus?",
            answer:
              "Untuk sistem on-grid (yang terhubung ke PLN), ya - ada proses ke PLN. Kami bantu urus dari A-Z: izin paralel, meteran ekspor-impor, hingga pelaporan ESDM. Sistem off-grid tidak butuh izin, langsung pasang."
          },
          {
            question: "Berapa lama panel surya bisa bertahan?",
            answer:
              "Modul panel berkualitas bisa bertahan 25-30 tahun dengan efisiensi di atas 80%. Inverter rata-rata 8-12 tahun. Selama sistem dirawat rutin, hasilnya stabil. Kami sediakan garansi 5-10 tahun tergantung merek & paket."
          },
          {
            question: "Bagaimana kalau cuaca mendung terus?",
            answer:
              "Tenang. Sistem tetap menghasilkan listrik meski tidak optimal. Untuk itu, kami hitung kapasitas dengan margin + estimasi cuaca. Bahkan mendung total pun sistem tetap aktif, hanya tidak 100%."
          }
        ].map((item, i) => (
          <details
            key={i}
            className="group border border-gray-200 rounded-xl p-4 transition-all open:shadow-sm open:bg-gray-50"
          >
            <summary className="font-semibold text-base cursor-pointer list-none flex justify-between items-center">
              <span>{item.question}</span>
              <span className="ml-2 transform group-open:rotate-180 transition-transform">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                </svg>
              </span>
            </summary>
            <p className="mt-3 text-sm">{item.answer}</p>
          </details>
        ))}
      </div>
    </ServiceSection>


    {/* CLIENT LOGO */}
    <ServiceSection id="client-logo" title="Dipercaya oleh Klien dari Berbagai Industri">
      <div className="mt-6">
        <Swiper
          spaceBetween={20}
          slidesPerView={2}
          breakpoints={{
            640: { slidesPerView: 3 },
            768: { slidesPerView: 4 },
            1024: { slidesPerView: 5 },
          }}
          loop
          autoplay={{ delay: 3000 }}
          className="opacity-80 grayscale hover:grayscale-0 transition-all"
        >
          {[
            "/images/clients/client-1.png",
            "/images/clients/client-2.png",
            "/images/clients/client-3.png",
            "/images/clients/client-4.png",
            "/images/clients/client-5.png",
            "/images/clients/client-6.png",
            "/images/clients/client-7.png",
            "/images/clients/client-8.png",
          ].map((logo, index) => (
            <SwiperSlide key={index}>
              <div className="flex justify-center items-center h-20">
                <img
                  src={logo}
                  alt={`Logo Klien Software Development ${index + 1}`}
                  className="max-h-12 object-contain"
                  loading="lazy"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>

    <ServiceSection id="testimoni" title="Apa Kata Klien Kami?">
      <div className="">
        <Swiper
          spaceBetween={20}
          slidesPerView={1.2}
          breakpoints={{
            768: { slidesPerView: 2.2 },
            1024: { slidesPerView: 3 },
          }}
          autoplay={{ delay: 6000 }}
          loop
        >
          {[
            {
              name: "Teguh Saputra",
              job: "Pemilik Kos-Kosan - Depok",
              comment:
                "Iseng nyoba panel surya di lantai 3, niatnya buat lampu koridor doang. Eh malah keterusan. Sekarang nyaris semua kamar nyala 24 jam tapi tagihan tetap irit. Nyesel sih… nyesel kenapa nggak pasang dari dulu 😅",
              image: "/images/testimonials/testimony-teguh.webp",
            },
            {
              name: "Andin Ramadhani",
              job: "Desainer Interior - Tangerang",
              comment:
                "Jujur, aku paling concern sama estetika. Nggak mau tuh rumah jadi kayak pabrik cuma karena pasang solar panel. Tapi tim ini bisa banget nge-mix antara fungsi dan desain. Kabel diselipin rapi, inverter-nya disatuin di lemari servis. Rapi, clean, dan nggak ngubah look fasad.",
              image: "/images/testimonials/testimony-andin.webp",
            },
            {
              name: "Bapak Yanto",
              job: "Ketua RW - Jakarta Selatan",
              comment:
                "Wkwkwk, awalnya warga sini tuh ngerasa solar panel itu mahal. Tapi pas dihitung-hitung, biaya listrik pos ronda dan lampu jalan itu nyedot terus. Jadi pakai sistem ini, iuran RT bisa turun. Malam jadi terang, dan aman. Enak buat ronda.",
              image: "/images/testimonials/testimony-yanto.webp",
            },
            {
              name: "Ruli Hartono",
              job: "Owner Kafe - Bekasi",
              comment:
                "Kafe kami sempat viral gegara tetap nyala pas daerah sekitar mati lampu. Banyak customer yang notice dan bilang: 'Wah, ini cafe ramah lingkungan ya?'. Padahal cuma gegara panel surya dan baterai backup. Tapi ya, efek branding-nya luar biasa.",
              image: "/images/testimonials/testimony-ruli.webp",
            },
          ].map((item, i) => (
            <SwiperSlide key={i} className="h-full flex self-stretch mb-4">
              <div className="bg-white border rounded-xl shadow-sm p-5 flex flex-col justify-between hover:shadow-md transition-all">
                <p className="text-sm leading-relaxed italic mb-4">"{item.comment}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.job}</p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>


    <ServiceSection id="kontak" title="Diskusi Proyek atau Booking Teknisi">
      <div className="grid md:grid-cols-2 gap-4 items-center mt-6">
        <div>
          <h3 className="text-2xl font-bold mb-4">
            Lagi cari teknisi yang nggak cuma bisa pasang, tapi mikir sistem?
          </h3>
          <p className="text-base mb-4">
            Kami bukan vendor. Kami adalah partner pertahananmu.
            Setiap kabel yang kami tarik, setiap kamera yang kami atur, dirancang untuk satu tujuan: **menenangkan pikiranmu.**
          </p>
          <ul className="text-sm space-y-1 mb-6 text-muted-foreground">
            <li>✅ Konsultasi langsung via WhatsApp, tanpa nunggu lama</li>
            <li>✅ Dapat estimasi waktu + biaya <strong>kurang dari 24 jam</strong></li>
            <li>✅ Booking teknisi di hari yang sama* (jika slot tersedia)</li>
          </ul>
          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%2C%20saya%20butuh%20bantuan%20untuk%20instalasi%20panel%20surya`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-green/90 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-white me-2" viewBox="0 0 16 16">
              <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
            </svg>
            Konsultasi Gratis
          </a>
          <p className="text-xs mt-3 text-muted-foreground">Aktif setiap hari, pukul 08.00 - 21.00 WIB</p>
        </div>

        <div className="relative w-full h-64 md:h-full rounded-xl overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.8420521856533!2d106.99524857499078!3d-6.284483493704425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e698d6d29d17095%3A0x765e1cdf4db9b9bb!2sGratia%20Global%20Solution!5e0!3m2!1sid!2sid!4v1754466163988!5m2!1sid!2sid"
            width="100%"
            height="100%"
            allowFullScreen
            loading="lazy"
            className="border-0 w-full h-full"
            title="Lokasi Kantor"
          ></iframe>
          <div className="absolute bottom-4 left-4 bg-white/90 text-sm px-4 py-2 rounded shadow">
            📍 Kantor Bekasi - Layanan ke Seluruh Jabodetabek
          </div>
        </div>
      </div>
    </ServiceSection>

  </>
);

export default JasaInstalasiPanelSuryaContent;